import React, { useEffect, useRef, useState } from 'react'
import Phaser from 'phaser'
import { useGameStore } from '../store/gameStore'
import { getMap, DEFAULT_MAP_ID } from '../game/model/mapCatalog'
import { getSession } from '../game/model/sessionHost'
import { PlayingScene } from '../game/scenes/PlayingScene'
import { GameOverScene } from '../game/scenes/GameOverScene'
import { PausedScene } from '../game/scenes/PausedScene'

/**
 * Phaser hosts match rendering only. Home/menu lives in React (HomeMenu).
 * Mounted only while gameState === 'playing'.
 */
export const GameCanvas: React.FC = () => {
  const gameRef = useRef<Phaser.Game | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const viewportRef = useRef<HTMLDivElement>(null)
  const [gameStarted, setGameStarted] = useState(false)

  const { gameState, resetGame, setGameSpeed, selectedMapId } = useGameStore()

  useEffect(() => {
    if (!containerRef.current || gameRef.current) return

    const parent = containerRef.current
    // Prefer real layout size; fall back to viewport so headless/automation
    // boots never create a 2×2 “blank” canvas from a zero-sized parent.
    const bootW = Math.max(parent.clientWidth || parent.parentElement?.clientWidth || 390, 32)
    const bootH = Math.max(parent.clientHeight || parent.parentElement?.clientHeight || 480, 32)
    const game = new Phaser.Game({
      type: Phaser.AUTO,
      parent,
      backgroundColor: '#0f172a',
      width: bootW,
      height: bootH,
      physics: {
        default: 'arcade',
        arcade: {
          gravity: { x: 0, y: 0 },
          debug: false
        }
      },
      scene: [PlayingScene, PausedScene, GameOverScene],
      scale: {
        mode: Phaser.Scale.RESIZE,
        autoCenter: Phaser.Scale.NO_CENTER,
        width: bootW,
        height: bootH
      },
      input: {
        activePointers: 3
      },
      render: {
        antialias: true,
        pixelArt: false,
        roundPixels: false
      }
    })
    gameRef.current = game

    if (import.meta.env.DEV) {
      Object.assign(globalThis, {
        __shapesTd: {
          startMatch: (mapId = DEFAULT_MAP_ID) => {
            const map = getMap(mapId)
            useGameStore.getState().startGame({
              difficulty: 'medium',
              mapId,
              startingLives: map?.startingLives ?? 20,
              startingCrystals: map?.startingGold ?? 0
            })
          },
          selectTower: (definitionId: string | null) => {
            useGameStore.getState().setPlacementDefinition(definitionId)
          },
          cancelPlacement: () => {
            useGameStore.getState().cancelPlacement()
          },
          placeAt: (definitionId: string, x: number, y: number) => {
            const session = getSession()
            if (!session) return { ok: false, reason: 'NO_SESSION' }
            useGameStore.getState().setPlacementDefinition(definitionId)
            const result = session.placeTower(definitionId, { x, y })
            useGameStore.getState().publishSessionView(session.view())
            if (result.ok) {
              useGameStore.getState().cancelPlacement()
              useGameStore.getState().selectTowerId(result.tower.id)
            }
            return result
          },
          selectPlaced: (towerId: string | null) => {
            useGameStore.getState().selectTowerId(towerId)
          },
          upgrade: (towerId?: string) => {
            const id = towerId ?? useGameStore.getState().selectedTowerId
            if (!id) return false
            return useGameStore.getState().upgradeTower(id)
          },
          sell: (towerId?: string) => {
            const id = towerId ?? useGameStore.getState().selectedTowerId
            if (!id) return false
            return useGameStore.getState().sellTower(id)
          },
          snapshot: (towerId?: string) => {
            const id = towerId ?? useGameStore.getState().selectedTowerId
            if (!id) return null
            return getSession()?.getTowerSnapshot(id) ?? null
          },
          session: () => getSession(),
          injectGold: (amount: number) => {
            const session = getSession()
            if (!session) return false
            session.gold += amount
            useGameStore.getState().publishSessionView(session.view())
            return true
          },
          startWave: () => useGameStore.getState().startWave(),
          waveDebug: () => getSession()?.waveDebug() ?? null,
          view: () => useGameStore.getState().sessionView,
          camera: () => {
            const scene = game.scene.getScene('PlayingScene') as PlayingScene
            return scene.getCameraDebug?.() ?? null
          },
          panBy: (dx: number, dy: number) => {
            const scene = game.scene.getScene('PlayingScene') as PlayingScene
            scene.debugPanBy?.(dx, dy)
          },
          game
        }
      })
    }

    game.events.on('ready', () => {
      setGameStarted(true)
    })

    game.events.on('resetGame', () => {
      resetGame()
    })

    game.events.on('setGameSpeed', (speed: number) => {
      setGameSpeed(speed)
    })

    return () => {
      if (import.meta.env.DEV) {
        delete (globalThis as { __shapesTd?: unknown }).__shapesTd
      }
      if (gameRef.current) {
        gameRef.current.destroy(true)
        gameRef.current = null
      }
      setGameStarted(false)
    }
  }, [])

  // Size the Phaser box to the map when the shell slot is taller than needed.
  useEffect(() => {
    const viewport = viewportRef.current
    const container = containerRef.current
    if (!viewport || !container) return

    const applySize = () => {
      const availW = viewport.clientWidth
      const availH = Math.max(viewport.clientHeight, 2)
      if (availW < 2) return

      // Fill the shell stage completely. Camera policy (square cells) lives in PlayingScene.
      // Do not shrink the CSS box to fitted map height — that created large unused voids.
      container.style.width = '100%'
      container.style.height = '100%'

      const syncPhaserSize = () => {
        const game = gameRef.current
        if (!game) return
        const w = Math.max(32, container.clientWidth || availW)
        const h = Math.max(32, container.clientHeight || availH)
        if (Math.abs(game.scale.width - w) > 1 || Math.abs(game.scale.height - h) > 1) {
          game.scale.resize(w, h)
        }
      }
      requestAnimationFrame(() => {
        syncPhaserSize()
        requestAnimationFrame(syncPhaserSize)
      })
    }

    applySize()
    const observer = new ResizeObserver(() => applySize())
    observer.observe(viewport)
    return () => observer.disconnect()
  }, [selectedMapId])

  useEffect(() => {
    if (!gameRef.current || !gameStarted) return

    switch (gameState) {
      case 'playing': {
        const playing = gameRef.current.scene.getScene('PlayingScene')
        const already =
          gameRef.current.scene.isActive('PlayingScene') ||
          gameRef.current.scene.isPaused('PlayingScene')
        if (gameRef.current.scene.isActive('GameOverScene')) {
          gameRef.current.scene.stop('GameOverScene')
        }
        if (gameRef.current.scene.isActive('PausedScene')) {
          gameRef.current.scene.stop('PausedScene')
        }
        if (!already) {
          gameRef.current.scene.start('PlayingScene', { mapId: selectedMapId })
        } else if (playing.scene.isPaused()) {
          playing.scene.resume()
        }
        break
      }

      case 'paused':
        if (gameRef.current.scene.isActive('PlayingScene')) {
          gameRef.current.scene.pause('PlayingScene')
        }
        // React GameMenu owns pause UI; avoid dual Phaser PausedScene overlays.
        if (gameRef.current.scene.isActive('PausedScene')) {
          gameRef.current.scene.stop('PausedScene')
        }
        break

      case 'gameOver':
      case 'victory':
        // React overlays own end-of-match UI; keep PlayingScene for backdrop.
        if (gameRef.current.scene.isActive('GameOverScene')) {
          gameRef.current.scene.stop('GameOverScene')
        }
        break
    }
  }, [gameState, gameStarted, selectedMapId])

  return (
    <div ref={viewportRef} className="game-viewport game-viewport--match">
      <div ref={containerRef} id="game-container" className="game-viewport__canvas">
        {!gameStarted && (
          <div className="game-viewport__loading">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-3" />
            <p className="text-slate-300 text-sm">Loading match...</p>
          </div>
        )}
      </div>
    </div>
  )
}
